import { useCallback, useEffect, useState } from "react";

const FAVORITES_KEY = "rk_favorites_v1";
const FAVORITES_EVENT = "rk:favorites-change";

function readList(key: string): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id): id is string => typeof id === "string");
  } catch {
    return [];
  }
}

function writeList(key: string, list: string[]) {
  try {
    window.localStorage.setItem(key, JSON.stringify(list));
  } catch {
    // storage may be unavailable; keep in-memory state only
  }
}

export function useFavorites() {
  const [favorites, setFavorites] = useState<string[]>(() => readList(FAVORITES_KEY));

  useEffect(() => {
    const sync = () => setFavorites(readList(FAVORITES_KEY));
    window.addEventListener(FAVORITES_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(FAVORITES_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const toggleFavorite = useCallback((id: string) => {
    const current = readList(FAVORITES_KEY);
    const next = current.includes(id) ? current.filter((item) => item !== id) : [id, ...current];
    writeList(FAVORITES_KEY, next);
    setFavorites(next);
    window.dispatchEvent(new Event(FAVORITES_EVENT));
    return next.includes(id);
  }, []);

  return {
    favorites,
    isFavorite: (id: string) => favorites.includes(id),
    toggleFavorite,
    clearFavorites: () => {
      writeList(FAVORITES_KEY, []);
      setFavorites([]);
      window.dispatchEvent(new Event(FAVORITES_EVENT));
    },
  };
}

const HISTORY_KEY = "rk_history_v1";
const HISTORY_EVENT = "rk:history-change";
const HISTORY_LIMIT = 8;

export function useViewHistory() {
  const [history, setHistory] = useState<string[]>(() => readList(HISTORY_KEY));

  useEffect(() => {
    const sync = () => setHistory(readList(HISTORY_KEY));
    window.addEventListener(HISTORY_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(HISTORY_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const addVisit = useCallback((id: string) => {
    const current = readList(HISTORY_KEY);
    const next = [id, ...current.filter((item) => item !== id)].slice(0, HISTORY_LIMIT);
    writeList(HISTORY_KEY, next);
    setHistory(next);
    window.dispatchEvent(new Event(HISTORY_EVENT));
  }, []);

  const clearHistory = useCallback(() => {
    writeList(HISTORY_KEY, []);
    setHistory([]);
    window.dispatchEvent(new Event(HISTORY_EVENT));
  }, []);

  return { history, addVisit, clearHistory };
}

const SEARCH_HISTORY_KEY = "rk_search_history_v1";
const SEARCH_EVENT = "rk:search-history-change";

export function useSearchHistory() {
  const [keywords, setKeywords] = useState<string[]>(() => readList(SEARCH_HISTORY_KEY));

  useEffect(() => {
    const sync = () => setKeywords(readList(SEARCH_HISTORY_KEY));
    window.addEventListener(SEARCH_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(SEARCH_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const addKeyword = useCallback((word: string) => {
    const value = word.trim();
    if (!value) return;
    const current = readList(SEARCH_HISTORY_KEY);
    const next = [value, ...current.filter((item) => item !== value)].slice(0, 6);
    writeList(SEARCH_HISTORY_KEY, next);
    setKeywords(next);
    window.dispatchEvent(new Event(SEARCH_EVENT));
  }, []);

  const clearKeywords = useCallback(() => {
    writeList(SEARCH_HISTORY_KEY, []);
    setKeywords([]);
    window.dispatchEvent(new Event(SEARCH_EVENT));
  }, []);

  return { keywords, addKeyword, clearKeywords };
}